import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface FleetSettingsData {
  company_id: string;
  notifications: {
    maintenance_alerts: boolean;
    fuel_alerts: boolean;
    document_expiry: boolean;
    violation_alerts: boolean;
    email_notifications: boolean;
    sms_notifications: boolean;
  };
  general: {
    timezone: string;
    date_format: string;
    currency: string;
    fuel_unit: string;
    distance_unit: string;
    working_hours_start: string;
    working_hours_end: string;
  };
  telemetry: {
    provider: string;
    api_key?: string;
    sync_interval: number;
    real_time_tracking: boolean;
  };
  permissions: {
    all_users_view_costs: boolean;
    restrict_vehicle_assignment: boolean;
    require_approval_maintenance: boolean;
  };
}

export function useFleetSettings(companyId?: string) {
  return useQuery({
    queryKey: ["fleetSettings", companyId],
    queryFn: async () => {
      if (!companyId) return null;
      
      // Fetch all settings for the company
      const { data, error } = await supabase
        .from("fleet_settings")
        .select("*")
        .eq("company_id", companyId);
      
      if (error) throw error;
      
      // Transform array of settings into structured object
      const settingsMap = data?.reduce((acc, setting) => {
        acc[setting.setting_key] = setting.setting_value;
        return acc;
      }, {} as Record<string, any>);

      // Return default settings if no settings found
      if (!settingsMap || Object.keys(settingsMap).length === 0) {
        return {
          company_id: companyId,
          notifications: {
            maintenance_alerts: true,
            fuel_alerts: true,
            document_expiry: true,
            violation_alerts: true,
            email_notifications: true,
            sms_notifications: false,
          },
          general: {
            timezone: "America/Sao_Paulo",
            date_format: "DD/MM/YYYY",
            currency: "BRL",
            fuel_unit: "L",
            distance_unit: "KM",
            working_hours_start: "08:00",
            working_hours_end: "18:00",
          },
          telemetry: {
            provider: "none",
            sync_interval: 300,
            real_time_tracking: false,
          },
          permissions: {
            all_users_view_costs: false,
            restrict_vehicle_assignment: true,
            require_approval_maintenance: true,
          }
        } as FleetSettingsData;
      }

      return {
        company_id: companyId,
        notifications: settingsMap.notifications || {},
        general: settingsMap.general || {},
        telemetry: settingsMap.telemetry || {},
        permissions: settingsMap.permissions || {}
      } as FleetSettingsData;
    },
    enabled: !!companyId,
  });
}

export function useSaveFleetSettings(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: FleetSettingsData) => {
      // Convert settings object to array of setting records
      const settingRecords = [
        {
          company_id: companyId,
          setting_group: 'notifications',
          setting_key: 'notifications',
          setting_value: settings.notifications,
          description: 'Notification preferences'
        },
        {
          company_id: companyId,
          setting_group: 'general',
          setting_key: 'general',
          setting_value: settings.general,
          description: 'General fleet settings'
        },
        {
          company_id: companyId,
          setting_group: 'telemetry',
          setting_key: 'telemetry',
          setting_value: settings.telemetry,
          description: 'Telemetry integration settings'
        },
        {
          company_id: companyId,
          setting_group: 'permissions',
          setting_key: 'permissions',
          setting_value: settings.permissions,
          description: 'Access control settings'
        }
      ];

      // Upsert each setting
      for (const record of settingRecords) {
        const { error } = await supabase
          .from("fleet_settings")
          .upsert(record, {
            onConflict: 'company_id,setting_key'
          });
        
        if (error) throw error;
      }

      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetSettings", companyId] });
      toast({
        title: "Sucesso",
        description: "Configurações salvas com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Error saving fleet settings:", error);
      toast({
        title: "Erro",
        description: "Erro ao salvar configurações.",
        variant: "destructive",
      });
    },
  });
}